import LoadingButton from '@app/src/components/LoadingButton'
import {
  BusinessOutlined,
  CheckOutlined,
  EuroOutlined,
  LanguageOutlined,
  NumbersOutlined,
  PeopleOutlined,
  TagOutlined,
} from '@mui/icons-material'
import { Box, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { ScheduleEnrichmentFormData } from './ScheduleEnrichmentModal'
import { SCHEDULE_ENRICHMENT_QUESTION_KEYS } from './ScheduleEnrichmentModal'

type EnrichmentInfoStepProps = {
  selectedCount: number
  isEnrichmentEnabled?: boolean
  onEnrichmentToggle?: (enabled: boolean) => void
  isLoading: boolean
  onSubmit: () => void
}

const ENRICHMENT_PROPERTIES = [
  { labelId: 'form.scheduleEnrichment.property.website', icon: <LanguageOutlined fontSize="small" /> },
  { labelId: 'form.scheduleEnrichment.property.industry', icon: <BusinessOutlined fontSize="small" /> },
  { labelId: 'form.scheduleEnrichment.property.registrationNumber', icon: <NumbersOutlined fontSize="small" /> },
  { labelId: 'form.scheduleEnrichment.property.vatNumber', icon: <TagOutlined fontSize="small" /> },
  { labelId: 'form.scheduleEnrichment.property.revenue', icon: <EuroOutlined fontSize="small" /> },
  { labelId: 'form.scheduleEnrichment.property.employees', icon: <PeopleOutlined fontSize="small" /> },
]

const BENEFITS = [
  'form.scheduleEnrichment.benefit.riskScreening',
  'form.scheduleEnrichment.benefit.noOverwrite',
  'form.scheduleEnrichment.benefit.reviewAnytime',
]

const EnrichmentInfoStep: React.FC<EnrichmentInfoStepProps> = ({
  selectedCount,
  isEnrichmentEnabled,
  onEnrichmentToggle,
  isLoading,
  onSubmit,
}) => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<ScheduleEnrichmentFormData>()

  const isControlled = isEnrichmentEnabled !== undefined
  const isChecked = isControlled ? isEnrichmentEnabled : true
  const isDisabled = !isControlled

  return (
    <Stack spacing={3}>
      <Box sx={{ bgcolor: 'grey.100', p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              disabled={isDisabled}
              onChange={e => onEnrichmentToggle?.(e.target.checked)}
            />
          }
          label={
            <Stack>
              <Typography variant="subtitle1" color="text.primary">
                {formatMessage({ id: 'form.scheduleEnrichment.info.enrichCompanies' }, { count: selectedCount })}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatMessage({ id: 'form.scheduleEnrichment.info.enrichCompaniesDescription' })}
              </Typography>
            </Stack>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </Box>

      <Stack spacing={1}>
        <Typography variant="subtitle1">
          {formatMessage({ id: 'form.scheduleEnrichment.info.propertiesTitle' })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {ENRICHMENT_PROPERTIES.map(property => (
            <Chip
              key={property.labelId}
              icon={property.icon}
              label={formatMessage({ id: property.labelId })}
              variant="outlined"
              sx={{ borderColor: 'grey.300' }}
            />
          ))}
        </Box>
      </Stack>

      <Stack spacing={1.5}>
        {BENEFITS.map(benefitId => (
          <Stack key={benefitId} direction="row" spacing={1} alignItems="center">
            <CheckOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography color="text.secondary">{formatMessage({ id: benefitId })}</Typography>
          </Stack>
        ))}
      </Stack>

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mt={4}>
        <Controller
          name={SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE}
          control={control}
          defaultValue={false}
          render={({ onChange, value }) => (
            <FormControlLabel
              control={<Checkbox checked={value} onChange={e => onChange(e.target.checked)} />}
              label={<Typography variant="body1">{formatMessage({ id: 'form.sendEmailWhenComplete' })}</Typography>}
            />
          )}
        />
        <LoadingButton loading={isLoading} variant="contained" onClick={onSubmit}>
          {formatMessage({ id: 'form.scheduleEnrichment.confirm.button' })}
        </LoadingButton>
      </Box>
    </Stack>
  )
}

export default EnrichmentInfoStep
